import { useState, useCallback, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { Button } from 'reactstrap';
import GoBack from '../../components/GoBack';
import { ADD, CHATGPT_TEMPLATES } from '../../constants';
import Loader from '../../components/layout/Loader';
import TableComponent from '../../components/layout/TableComponent';
import {
  MODULE_HEADER_CHATGPT,
  SETTINGS_ROUTE,
  chatGPtDTO,
} from '../../utils/constant';
import EDIT_ICON from '../../assets/images/edit-icon-menu.svg';
import DrawerComponent from './Drawer';
import { ALL_CHATGPT_TEMPLATES, ALL_LLM_PROVIDERS } from './gql';
import { useLazyQuery, useQuery } from '@apollo/client';
import { FormValuesModule, IChatGPTTemplateSetting } from '../../interfaces';
import { Controller, useForm } from 'react-hook-form';
import ModalComponent from '../../common/Modal';
import AddModal from './AddModal';
import { useContext } from 'react';
import { AppContext } from '../../context';
import { customSelectStyleNew } from '../../components/styled/customSelect';

const ChatGPT_Templates = () => {
  const [addModule, setAddModule] = useState<boolean>(false);
  const [allChatGptTemplates, { data: templatesData, loading: listLoading, refetch: refetchGPTTemplates }] =
    useLazyQuery(ALL_CHATGPT_TEMPLATES);
  const { data: providersData } = useQuery(ALL_LLM_PROVIDERS);
  const { handleSubmit, control } = useForm<FormValuesModule>();
  const [data, setData] = useState<IChatGPTTemplateSetting>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [filterProvider, setFilterProvider] = useState<any>(null);
  const [filterModel, setFilterModel] = useState<any>(null);
  const { theme } = useContext(AppContext);

  const fetchingTemplatesData = useCallback(async () => {
    await allChatGptTemplates();
  }, [allChatGptTemplates]);

  useEffect(() => {
    fetchingTemplatesData();
  }, [fetchingTemplatesData]);

  const providerOptions = useMemo(() =>
    (providersData?.allLlmProviders ?? []).map((p: any) => ({ label: p.displayName, value: p.id, name: p.name })),
    [providersData]
  );

  const modelOptions = useMemo(() => {
    if (!filterProvider) return [];
    const templates = templatesData?.AllChatGptTemplates?.chatGPTtemplates ?? [];
    const seen = new Set<string>();
    const opts: any[] = [];
    for (const t of templates) {
      if (t.model?.provider?.id === filterProvider.value && !seen.has(t.model.id)) {
        seen.add(t.model.id);
        opts.push({ label: t.model.displayName, value: t.model.id });
      }
    }
    return opts;
  }, [filterProvider, templatesData]);

  const filteredTemplates = useMemo(() => {
    let list = templatesData?.AllChatGptTemplates?.chatGPTtemplates ?? [];
    if (filterProvider) {
      list = list.filter((t: any) => t.model?.provider?.id === filterProvider.value);
    }
    if (filterModel) {
      list = list.filter((t: any) => t.model?.id === filterModel.value);
    }
    // Sort by the displayed name (same expression the table cell renders), so the
    // list reads alphabetically. Spread first — `list` may still be the Apollo
    // cache array when no filter is applied, which must not be sorted in place.
    const nameOf = (t: any) => String(t.name ?? chatGPtDTO[t.module] ?? t.module ?? '');
    return [...list].sort((a: any, b: any) =>
      nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' }),
    );
  }, [templatesData, filterProvider, filterModel]);

  if (listLoading) {
    return <Loader />;
  }

  return (
    <>
      <div className="admin-tabs team-tabs">
        <GoBack url={SETTINGS_ROUTE} />

        <div className="primaryHeading">
          <h5 className="m-0">{CHATGPT_TEMPLATES}</h5>
        </div>

        <div className="search-filter primaryHeaderSpacing">
          <div className="d-flex gap-3">
            <div style={{ minWidth: 200 }}>
              <Select
                isClearable
                placeholder="Filter by LLM"
                options={providerOptions}
                value={filterProvider}
                onChange={(val) => { setFilterProvider(val); setFilterModel(null); }}
                styles={{ ...customSelectStyleNew(theme) }}
              />
            </div>
            <div style={{ minWidth: 200 }}>
              <Select
                isClearable
                placeholder="Filter by Model"
                options={modelOptions}
                value={filterModel}
                isDisabled={!filterProvider}
                onChange={(val) => setFilterModel(val)}
                styles={{ ...customSelectStyleNew(theme) }}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpenModal(!openModal)}
            className="buttonGenericStyle filledButton"
          >
            {ADD}
          </button>
        </div>

        <div className="mr-3">
          <TableComponent tableHeader={MODULE_HEADER_CHATGPT}>
            <tbody>
              {filteredTemplates.map((item: any, index: number) => (
                <tr key={item.id}>
                  <td>{item.name ?? chatGPtDTO[item.module] ?? item.module}</td>
                  <td>{item.model?.provider?.displayName ?? '—'}</td>
                  <td>{item.model?.displayName ?? '—'}</td>
                  <td className="text-truncate max-200">{item.description ?? '—'}</td>
                  <td className="text-truncate max-200">
                    {item.updatedAt ? item.updatedAt : item.createdAt}
                  </td>
                  <td>
                    <Button className="bg-transparent border-0 p-0" onClick={() => {
                      setData(filteredTemplates[index]);
                      setAddModule(!addModule);
                    }}>
                      <img src={EDIT_ICON} alt="edit" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableComponent>
        </div>
      </div>

      <DrawerComponent
        isOpen={addModule}
        toggle={() => setAddModule(!addModule)}
        data={data}
        refetchGPTTemplates={refetchGPTTemplates}
      />

      <ModalComponent size="md" isOpen={openModal} toggle={() => setOpenModal(false)}>
        <AddModal
          onClose={() => setOpenModal(false)}
          Controller={Controller}
          control={control}
          handleSubmit={handleSubmit}
          refetchGPTTemplates={refetchGPTTemplates}
          toggle={() => setOpenModal(false)}
        />
      </ModalComponent>
    </>
  );
};

export default ChatGPT_Templates;
